import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { setToken, formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [error, setError] = useState("");

  // `api` is a module-level import and `setUser` is a stable dispatcher from
  // useState, so an empty deps array is intentional here.
  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.token) setToken(data.token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Logout failure is non-fatal — we clear local state anyway so the user
      // is signed out from the client's perspective. Logging for observability.
      console.warn("Logout API call failed (clearing local state regardless):", e);
    }
    setToken(null);
    setUser(false);
  };

  // Memoize the context value so consumers do not re-render on unrelated
  // parent renders.
  const value = useMemo(
    () => ({ user, login, logout, error, setError }),
    // login/logout/setError are stable in practice (defined per render but
    // only consumed in callbacks). Including only `user` and `error` is the
    // dependency set that actually moves.
    [user, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
